import { useMemo, forwardRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { format2 } from '@/utils/charts';
import type { PvDailyRow, StorageDailyRow, AGCStorageDetailRow } from '@/types/api';

interface RevenuePrintViewProps {
  pvRows: PvDailyRow[];
  storageRows: StorageDailyRow[];
  singleDayDetailRows?: AGCStorageDetailRow[];
  isSingleDay: boolean;
  displayStation: string;
  startDate: string;
  endDate: string;
  batteryChartData?: Record<string, AGCStorageDetailRow[]>;
  interprovPeriods?: Array<{period: string; power: number; price: number}>;
  agcPdiff?: number;
}

const PAGE_WIDTH = 794;
const PAD_X = 24;
const PAD_Y = 16;

/* 固定紧凑布局策略（硬性要求：31天也必须1页A4）
   预算：标题30 + KPI32 + 图表区(~210多日 / ~340单日) + 表格(~480多日31行 / ~50单日1行)
        + 间距30 + 页脚10 = ~800px，余量充裕 */

const RevenuePrintView = forwardRef<HTMLDivElement, RevenuePrintViewProps>(
  ({ pvRows, storageRows, singleDayDetailRows, isSingleDay, displayStation, startDate, endDate, batteryChartData, interprovPeriods, agcPdiff }, ref) => {
    const merged = useMemo(() => {
      const map = new Map<
        string,
        {
          date: string;
          pvRt: number;
          pvSub: number;
          pvTotal: number;
          pvEnergy: number;
          v: number;
          e: number;
          s: number;
          a: number;
          stTotal: number;
          stEnergy: number;
        }
      >();

      for (const r of pvRows) {
        const d = typeof r['日期'] === 'string' ? r['日期'] : String(r['日期']);
        map.set(d, {
          date: d,
          pvRt: Number(r['实时电费(元)']) || 0,
          pvSub: Number(r['场外补贴电费(元)']) || 0,
          pvTotal: Number(r['总收益(元)']) || 0,
          pvEnergy: Number(r['总上网电量(MWh)']) || 0,
          v: 0, e: 0, s: 0, a: 0, stTotal: 0, stEnergy: 0,
        });
      }

      for (const r of storageRows) {
        const d = typeof r['日期'] === 'string' ? r['日期'] : String(r['日期']);
        const existing = map.get(d);
        const v = Number(r['虚报挽回(元)']) || 0;
        const e = Number(r['现货净利(元)']) || 0;
        const s = Number(r['机制补贴(元)']) || 0;
        const a = Number(r['规避考核(元)']) || 0;
        const stEnergy = Number(r['总放电量(MWh)']) || 0;
        // 汇总页剔除虚报挽回和规避考核，与 RevenueSummaryTab 保持一致
        const stTotalClean = e + s;
        if (existing) {
          existing.v = v; existing.e = e; existing.s = s; existing.a = a;
          existing.stTotal = stTotalClean; existing.stEnergy = stEnergy;
        } else {
          map.set(d, {
            date: d,
            pvRt: 0, pvSub: 0, pvTotal: 0, pvEnergy: 0,
            v, e, s, a, stTotal: stTotalClean, stEnergy,
          });
        }
      }

      return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
    }, [pvRows, storageRows]);

    const totals = useMemo(() => {
      let totalPv = 0, totalSt = 0, totalAll = 0;
      let totalPvEnergy = 0, totalStEnergy = 0;
      for (const r of merged) {
        totalPv += r.pvTotal;
        totalSt += r.stTotal;
        totalAll += r.pvTotal + r.stTotal;
        totalPvEnergy += r.pvEnergy;
        totalStEnergy += r.stEnergy;
      }
      const totalEnergy = totalPvEnergy + totalStEnergy;
      return {
        totalPv,
        totalSt,
        totalAll,
        totalEnergy,
        totalPvEnergy,
        totalStEnergy,
        totalPrice: totalEnergy ? totalAll / totalEnergy : 0,
        pvPrice: totalPvEnergy ? totalPv / totalPvEnergy : 0,
        stPrice: totalStEnergy ? totalSt / totalStEnergy : 0,
      };
    }, [merged]);

    const xLabels = useMemo(
      () =>
        merged.map((r) => {
          const d = new Date(r.date + 'T00:00:00');
          return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }),
      [merged]
    );

    const titleDates = useMemo(() => {
      const s = startDate.replace(/-/g, '').slice(4);
      const e = endDate.replace(/-/g, '').slice(4);
      return startDate === endDate ? s : `${s}-${e}`;
    }, [startDate, endDate]);

    // ── 汇总图表 options（超紧凑打印版） ──
    const trendOption: EChartsOption = useMemo(() => {
      if (!merged.length) return {};
      return {
        tooltip: { trigger: 'axis' },
        grid: { left: 44, right: 4, top: 10, bottom: 18 },
        xAxis: {
          type: 'category',
          data: xLabels,
          axisLabel: { fontSize: 7, color: '#666', interval: 'auto' },
          axisLine: { lineStyle: { color: '#ccc' } },
        },
        yAxis: {
          type: 'value',
          axisLabel: { fontSize: 7, color: '#666' },
          splitLine: { lineStyle: { color: '#eee' } },
        },
        series: [
          {
            name: '全场总收益',
            type: 'line',
            data: merged.map((r) => r.pvTotal + r.stTotal),
            smooth: true,
            lineStyle: { color: '#0d9488', width: 1.5 },
            areaStyle: { color: 'rgba(13,148,136,0.10)' },
            itemStyle: { color: '#0d9488' },
            showSymbol: false,
          },
        ],
      };
    }, [merged, xLabels]);

    const pieOption: EChartsOption = useMemo(() => {
      const data = [
        { name: '光伏实时电费', value: merged.reduce((s, r) => s + r.pvRt, 0) },
        { name: '光伏场外补贴', value: merged.reduce((s, r) => s + r.pvSub, 0) },
        { name: '储能实时收入', value: merged.reduce((s, r) => s + r.e, 0) },
        { name: '储能补贴', value: merged.reduce((s, r) => s + r.s, 0) },
      ].filter((d) => Number.isFinite(d.value) && d.value !== 0);
      if (!data.length) return {};
      return {
        tooltip: { trigger: 'item' },
        legend: {
          orient: 'vertical',
          right: 0,
          top: 'middle',
          textStyle: { fontSize: 7, color: '#374151' },
          itemWidth: 6,
          itemHeight: 6,
        },
        series: [
          {
            type: 'pie',
            radius: ['28%', '50%'],
            center: ['38%', '50%'],
            data,
            label: { show: false },
          },
        ],
      };
    }, [merged]);

    const barOption: EChartsOption = useMemo(() => {
      if (!merged.length) return {};
      return {
        tooltip: { trigger: 'axis' },
        grid: { left: 44, right: 4, top: 10, bottom: 18 },
        xAxis: {
          type: 'category',
          data: xLabels,
          axisLabel: { fontSize: 7, color: '#666', interval: 'auto' },
          axisLine: { lineStyle: { color: '#ccc' } },
        },
        yAxis: {
          type: 'value',
          name: 'MWh',
          nameTextStyle: { fontSize: 7 },
          axisLabel: { fontSize: 7, color: '#666' },
          splitLine: { lineStyle: { color: '#eee' } },
        },
        series: [
          {
            name: '光伏上网',
            type: 'bar',
            stack: 'total',
            data: merged.map((r) => r.pvEnergy),
            itemStyle: { color: '#0d9488' },
          },
          {
            name: '配储放电',
            type: 'bar',
            stack: 'total',
            data: merged.map((r) => r.stEnergy),
            itemStyle: { color: '#374151' },
          },
        ],
      };
    }, [merged, xLabels]);

    // ── 单日明细图表（超紧凑） ──
    const detailRows = singleDayDetailRows || [];
    const times = useMemo(() => detailRows.map((r) => r.time), [detailRows]);

    const storageChart1Option: EChartsOption = useMemo(() => {
      if (!detailRows.length) return {};
      return {
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['光伏可用', '光伏实际', 'AGC目标', '并网功率', '超短期预测', '储能出力'],
          textStyle: { fontSize: 6 },
          top: 0,
          itemWidth: 6,
          itemHeight: 6,
        },
        grid: { left: 28, right: 4, top: 26, bottom: 20 },
        xAxis: {
          type: 'category',
          data: times,
          axisLabel: { fontSize: 5, rotate: 45, interval: 11 },
          axisLine: { lineStyle: { color: '#ccc' } },
        },
        yAxis: {
          type: 'value',
          name: 'MW',
          nameTextStyle: { fontSize: 6 },
          axisLabel: { fontSize: 6 },
          splitLine: { lineStyle: { color: '#eee' } },
        },
        series: [
          {
            name: '光伏可用', type: 'line',
            data: detailRows.map((r) => r.pv_avail),
            lineStyle: { color: '#13c2c2', type: 'dashed', width: 0.8 },
            itemStyle: { color: '#13c2c2' }, showSymbol: false,
          },
          {
            name: '光伏实际', type: 'line',
            data: detailRows.map((r) => r.pv_act),
            areaStyle: { color: 'rgba(24,144,255,0.10)' },
            lineStyle: { color: '#1890ff', width: 0.8 },
            itemStyle: { color: '#1890ff' }, showSymbol: false,
          },
          {
            name: 'AGC目标', type: 'line',
            data: detailRows.map((r) => r.agc_target),
            lineStyle: { color: '#faad14', width: 1.2 },
            itemStyle: { color: '#faad14' }, showSymbol: false,
          },
          {
            name: '并网功率', type: 'line',
            data: detailRows.map((r) => r.agc_act),
            lineStyle: { color: '#f5222d', width: 0.8 },
            itemStyle: { color: '#f5222d' }, showSymbol: false,
          },
          {
            name: '超短期预测', type: 'line',
            data: detailRows.map((r) => r.ust),
            lineStyle: { color: '#52c41a', type: 'dotted', width: 0.8 },
            itemStyle: { color: '#52c41a' }, showSymbol: false,
          },
          {
            name: '储能出力', type: 'bar',
            data: detailRows.map((r) => r.bat),
            itemStyle: { color: 'rgba(114,46,209,0.30)' },
          },
        ],
      };
    }, [detailRows, times]);

    const storageChart2Option: EChartsOption = useMemo(() => {
      if (!detailRows.length) return {};
      return {
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['储能出力', '日前申报', '实时电价'],
          textStyle: { fontSize: 6 },
          top: 0,
          itemWidth: 6,
          itemHeight: 6,
        },
        grid: { left: 28, right: 40, top: 26, bottom: 20 },
        xAxis: {
          type: 'category',
          data: times,
          axisLabel: { fontSize: 5, rotate: 45, interval: 11 },
          axisLine: { lineStyle: { color: '#ccc' } },
        },
        yAxis: [
          {
            type: 'value', name: 'MW', nameTextStyle: { fontSize: 6 },
            axisLabel: { fontSize: 6 },
            splitLine: { lineStyle: { color: '#eee' } },
          },
          {
            type: 'value', name: '元/MWh', nameTextStyle: { fontSize: 6 },
            axisLabel: { fontSize: 6 },
            splitLine: { show: false },
          },
        ],
        series: [
          {
            name: '储能出力', type: 'bar',
            data: detailRows.map((r) => r.bat),
            itemStyle: { color: 'rgba(114,46,209,0.30)' },
            yAxisIndex: 0,
          },
          {
            name: '日前申报', type: 'line',
            data: detailRows.map((r) => r.ep),
            lineStyle: { color: '#f5222d', width: 1.2 },
            itemStyle: { color: '#f5222d' }, showSymbol: false,
            yAxisIndex: 0,
          },
          {
            name: '实时电价', type: 'line',
            data: detailRows.map((r) => r.p),
            lineStyle: { color: '#52c41a', type: 'dashed', width: 0.8 },
            itemStyle: { color: '#52c41a' }, showSymbol: false,
            yAxisIndex: 1,
          },
        ],
      };
    }, [detailRows, times]);

    // ── 单日 24 小时收益瀑布（PDF 紧凑版） ──
    const hourBuckets = useMemo(() => {
      if (!isSingleDay) return [];
      const buckets = Array.from({ length: 24 }, () => ({ pvE: 0, pvRev: 0, e: 0, s: 0 }));
      const ipMap: Record<string, { power: number; price: number }> = {};
      for (const ip of interprovPeriods ?? []) {
        ipMap[ip.period] = { power: ip.power, price: ip.price };
      }
      const pdiff = agcPdiff ?? 0;

      for (const r of detailRows) {
        const hh = parseInt(r.time.slice(0, 2), 10);
        if (Number.isNaN(hh) || hh < 0 || hh > 23) continue;
        const gridMw = (r.agc_act || 0) - Math.max(0, r.bat || 0);
        const min = parseInt(r.time.slice(3, 5), 10);
        const slotMin = Math.floor(min / 15) * 15;
        const period = `${String(hh).padStart(2, '0')}:${String(slotMin).padStart(2, '0')}`;
        const ip = ipMap[period];
        const gridMwh = gridMw * (5 / 60);
        const ipMw = ip ? ip.power / 4 : 0;
        const ipMwh = gridMwh > 0 ? Math.min(ipMw * (5 / 60), gridMwh) : 0;
        const internalE = gridMwh - ipMwh;
        buckets[hh].pvRev += internalE * ((r.p || 0) + pdiff);
        buckets[hh].pvRev += ipMwh * (ip?.price || 0);
        buckets[hh].pvE += gridMwh;
        buckets[hh].e += r.e || 0;
        buckets[hh].s += r.s || 0;
      }
      return buckets;
    }, [isSingleDay, detailRows, interprovPeriods, agcPdiff]);

    const hourlyWaterfallOption: EChartsOption = useMemo(() => {
      if (!isSingleDay || !hourBuckets.length) return {};
      const hourlyValues = hourBuckets.map((b) => b.pvRev + b.e + b.s);
      if (hourlyValues.every((v) => v === 0)) return {};
      const totalAll = hourlyValues.reduce((s, v) => s + v, 0);
      const helpers = hourlyValues.map((_, i) =>
        i === 0 ? 0 : hourlyValues.slice(0, i).reduce((a, b) => a + b, 0)
      );
      helpers.push(0);
      const categories = [...Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}时`), '合计'];
      const values = [...hourlyValues, totalAll];
      return {
        tooltip: { trigger: 'axis' },
        grid: { left: 44, right: 4, top: 10, bottom: 22 },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: { fontSize: 6, color: '#666', interval: 1, rotate: 45 },
          axisLine: { lineStyle: { color: '#ccc' } },
        },
        yAxis: {
          type: 'value',
          name: '元',
          nameTextStyle: { fontSize: 7 },
          axisLabel: { fontSize: 7, color: '#666' },
          splitLine: { lineStyle: { color: '#eee' } },
        },
        series: [
          { name: '辅助', type: 'bar', stack: 'total', itemStyle: { color: 'transparent' }, data: helpers },
          {
            name: '收益', type: 'bar', stack: 'total',
            data: values.map((v, i) => ({
              value: v,
              itemStyle: { color: i === values.length - 1 ? '#0d9488' : v >= 0 ? '#0d9488' : '#ef4444' },
            })),
          },
        ],
      };
    }, [isSingleDay, hourBuckets, pvRows]);

    const energyPieOption: EChartsOption = useMemo(() => {
      if (!isSingleDay) return {};
      const pvE = Number(pvRows[0]?.['总上网电量(MWh)']) || 0;
      const stE = Number(storageRows[0]?.['总放电量(MWh)']) || 0;
      const data = [
        { name: '光伏上网电量', value: pvE, itemStyle: { color: '#0d9488' } },
        { name: '储能放电量', value: stE, itemStyle: { color: '#374151' } },
      ].filter((d) => Number.isFinite(d.value) && d.value > 0);
      if (!data.length) return {};
      return {
        tooltip: { trigger: 'item' },
        legend: {
          orient: 'vertical',
          right: 0,
          top: 'middle',
          textStyle: { fontSize: 7, color: '#374151' },
          itemWidth: 6,
          itemHeight: 6,
        },
        series: [
          {
            type: 'pie',
            radius: ['28%', '50%'],
            center: ['38%', '50%'],
            data,
            label: { show: false },
          },
        ],
      };
    }, [isSingleDay, pvRows, storageRows]);

    // ── 配储分析图（PDF紧凑版，多日 2-7 天） ──
    const batteryChartOption: EChartsOption = useMemo(() => {
      const data = batteryChartData || {};
      const dates = Object.keys(data).sort();
      if (dates.length === 0) return {};

      const xLabels: string[] = [];
      const batSeries: number[] = [];
      const priceSeries: number[] = [];

      for (const d of dates) {
        const rows = data[d];
        if (!rows || rows.length === 0) continue;
        const mmdd = d.slice(5);
        const hourly: { batEnergy: number; p: number }[] = Array.from({ length: 24 }, () => ({ batEnergy: 0, p: 0 }));
        for (const r of rows) {
          const hh = parseInt(r.time.slice(0, 2), 10);
          if (Number.isNaN(hh) || hh < 0 || hh > 23) continue;
          hourly[hh].batEnergy += (r.bat || 0) * (5 / 60);
        }
        const pricesByHour: Record<number, number[]> = {};
        for (const r of rows) {
          const hh = parseInt(r.time.slice(0, 2), 10);
          if (Number.isNaN(hh)) continue;
          if (!pricesByHour[hh]) pricesByHour[hh] = [];
          pricesByHour[hh].push(r.p || 0);
        }
        for (let h = 0; h < 24; h++) {
          const pvals = pricesByHour[h] || [];
          hourly[h].p = pvals.length > 0 ? pvals.reduce((a, b) => a + b, 0) / pvals.length : 0;
          xLabels.push(`${mmdd} ${String(h).padStart(2, '0')}时`);
          batSeries.push(hourly[h].batEnergy);
          priceSeries.push(hourly[h].p);
        }
      }

      const batColors = batSeries.map((v) => (v < 0 ? '#22c55e' : '#ef4444'));

      return {
        tooltip: { trigger: 'axis' },
        grid: { left: 44, right: 44, top: 20, bottom: 28 },
        xAxis: {
          type: 'category', data: xLabels,
          axisLabel: { fontSize: 5, color: '#666', interval: 23, rotate: 45 },
        },
        yAxis: (() => {
          const vp = priceSeries.filter((v) => Number.isFinite(v));
          const pMax = vp.length > 0 ? Math.max(...vp) : 500;
          const pCeil = Math.ceil(pMax / 100) * 100 + 100;
          return [
            { type: 'value', name: '元/MWh', nameTextStyle: { fontSize: 6 }, axisLabel: { fontSize: 6 }, min: -pCeil, max: pCeil, splitLine: { lineStyle: { color: '#eee' } } },
            { type: 'value', name: 'MWh', nameTextStyle: { fontSize: 6 }, axisLabel: { fontSize: 6 }, min: -100, max: 100, splitLine: { show: false } },
          ];
        })(),
        legend: { data: ['电价', '储能'], textStyle: { fontSize: 6 }, top: 0, itemWidth: 6, itemHeight: 6 },
        series: [
          { name: '电价', type: 'line', data: priceSeries, lineStyle: { color: '#f97316', width: 1 }, itemStyle: { color: '#f97316' }, symbol: 'none', yAxisIndex: 0 },
          { name: '储能', type: 'bar', data: batSeries.map((v, i) => ({ value: v, itemStyle: { color: batColors[i] } })), barWidth: '80%', yAxisIndex: 1 },
        ],
      };
    }, [batteryChartData]);

    const hasBatteryChart = useMemo(() => {
      const data = batteryChartData || {};
      return Object.keys(data).length > 0;
    }, [batteryChartData]);

    // ── 汇总表 ──
    const tableData = useMemo(() => {
      const rows = merged.map((r) => ({
        date: r.date,
        totalEnergy: r.pvEnergy + r.stEnergy,
        totalRt: r.pvRt + r.e,
        totalSub: r.pvSub + r.s,
        totalAll: r.pvTotal + r.stTotal,
        pvTotal: r.pvTotal,
        stTotal: r.stTotal,
      }));
      const sum = {
        date: '合计',
        totalEnergy: rows.reduce((s, r) => s + r.totalEnergy, 0),
        totalRt: rows.reduce((s, r) => s + r.totalRt, 0),
        totalSub: rows.reduce((s, r) => s + r.totalSub, 0),
        totalAll: rows.reduce((s, r) => s + r.totalAll, 0),
        pvTotal: rows.reduce((s, r) => s + r.pvTotal, 0),
        stTotal: rows.reduce((s, r) => s + r.stTotal, 0),
      };
      return [...rows, sum];
    }, [merged]);

    const tableColumns = [
      { title: '日期', key: 'date', width: 80 },
      { title: '总上网电量', key: 'totalEnergy', width: 68, align: 'right' as const },
      { title: '总实时电费', key: 'totalRt', width: 68, align: 'right' as const },
      { title: '总机制补偿', key: 'totalSub', width: 68, align: 'right' as const },
      { title: '总收入', key: 'totalAll', width: 68, align: 'right' as const },
      { title: '光伏收入', key: 'pvTotal', width: 68, align: 'right' as const },
      { title: '储能收入', key: 'stTotal', width: 68, align: 'right' as const },
    ];

    const colgroup = tableColumns.map((c) => <col key={c.key} style={{ width: c.width }} />);

    const thead = (
      <thead>
        <tr style={{ background: '#f3f4f6' }}>
          {tableColumns.map((c) => (
            <th
              key={c.key}
              style={{
                padding: '4px 6px',
                fontSize: 9,
                fontWeight: 600,
                color: '#374151',
                textAlign: c.align || 'left',
                border: '1px solid #d1d5db',
              }}
            >
              {c.title}
            </th>
          ))}
        </tr>
      </thead>
    );

    const tbody = (
      <tbody>
        {tableData.map((row, idx) => (
          <tr
            key={row.date + idx}
            style={{
              background: row.date === '合计' ? '#f9fafb' : idx % 2 === 0 ? '#fff' : '#fafafa',
              fontWeight: row.date === '合计' ? 700 : 400,
            }}
          >
            {tableColumns.map((c) => (
              <td
                key={c.key}
                style={{
                  padding: '3px 6px',
                  fontSize: 9,
                  color: '#374151',
                  textAlign: c.align || 'left',
                  border: '1px solid #d1d5db',
                }}
              >
                {c.key === 'date'
                  ? row.date
                  : format2(row[c.key as keyof typeof row] as number)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );

    // 固定紧凑高度（不再动态计算，确保31天也能放下）
    const G = 16;
    const PAGE_H = 1122; // A4 比例（794 × 297/210）
    const TABLE_ROW_H = 15;
    const tableRows = tableData.length;
    const tableH = tableRows * TABLE_ROW_H + 40; // 表头+标题
    const fixedH = PAD_Y * 2 + 34 + 60 + 20 + G * 3 + tableH; // 边距+标题+KPI+页脚+间距+汇总表

    const CH1_H = isSingleDay ? 190 : 155;

    // 多日有配储图时，电量和配储两张图动态填满剩余空间
    const expandCharts = !isSingleDay && hasBatteryChart;
    const remainingH = Math.max(0, PAGE_H - fixedH - CH1_H);
    const CH2_H = isSingleDay ? 150
      : expandCharts ? Math.max(130, Math.floor(remainingH / 2) - G)
      : 130;
    const BATTERY_CHART_H = expandCharts ? Math.max(130, Math.floor(remainingH / 2) - G) : 130;
    const CH3_H = 220;
    const CH4_H = 200;

    const chartBoxStyle: React.CSSProperties = {
      border: '1px solid #e5e7eb',
      borderRadius: 6,
      background: '#fff',
      padding: '4px 6px 6px',
      boxSizing: 'border-box',
    };

    return (
      <div
        ref={ref}
        style={{
          width: PAGE_WIDTH,
          padding: `${PAD_Y}px ${PAD_X}px`,
          background: '#fff',
          fontFamily: '"Microsoft YaHei", "PingFang SC", sans-serif',
          boxSizing: 'border-box',
          color: '#1f2937',
        }}
      >
        {/* 标题 */}
        <div
          style={{
            textAlign: 'center',
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 16,
            borderBottom: '2px solid #0d9488',
            paddingBottom: 10,
          }}
        >
          {displayStation}场站 {titleDates} 收益测算
        </div>

        {/* KPI */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: G,
            padding: '14px 0',
            background: '#f0fdfa',
            borderRadius: 8,
          }}
        >
          {/* 全场总收益 */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#6b7280' }}>全场总收益</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0d9488' }}>{format2(totals.totalAll)} 元</div>
            <div style={{ marginTop: 2, textAlign: 'left', fontSize: 8, color: '#6b7280', lineHeight: 1.5 }}>
              <div>光伏 {format2(totals.totalPv)} 元</div>
              <div>储能 {format2(totals.totalSt)} 元</div>
            </div>
          </div>
          {/* 总上网电量 */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#6b7280' }}>总上网电量</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0d9488' }}>{format2(totals.totalEnergy)} MWh</div>
            <div style={{ marginTop: 2, textAlign: 'left', fontSize: 8, color: '#6b7280', lineHeight: 1.5 }}>
              <div>光伏 {format2(totals.totalPvEnergy)} MWh</div>
              <div>储能 {format2(totals.totalStEnergy)} MWh</div>
            </div>
          </div>
          {/* 综合电价 */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: '#6b7280' }}>综合电价</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0d9488' }}>{format2(totals.totalPrice)} 元/MWh</div>
            <div style={{ marginTop: 2, textAlign: 'left', fontSize: 8, color: '#6b7280', lineHeight: 1.5 }}>
              <div>光伏 {format2(totals.pvPrice)} 元/MWh</div>
              <div>储能 {format2(totals.stPrice)} 元/MWh</div>
            </div>
          </div>
        </div>

        {/* 图表区 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: G }}>
          {isSingleDay ? (
            <>
              {/* 第1行：24小时瀑布（全宽） */}
              <div style={{ ...chartBoxStyle, width: '100%', height: CH1_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  24小时收益瀑布
                </div>
                <ReactECharts option={hourlyWaterfallOption} style={{ height: CH1_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>

              {/* 第2行：电量结构占比 + 收益结构占比 */}
              <div style={{ ...chartBoxStyle, width: `calc(50% - ${G / 2}px)`, height: CH2_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  电量结构占比
                </div>
                <ReactECharts option={energyPieOption} style={{ height: CH2_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>
              <div style={{ ...chartBoxStyle, width: `calc(50% - ${G / 2}px)`, height: CH2_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  收益结构占比
                </div>
                <ReactECharts option={pieOption} style={{ height: CH2_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>

              {/* 第3行：AGC（全宽） */}
              <div style={{ ...chartBoxStyle, width: '100%', height: CH3_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  物理执行与AGC复盘
                </div>
                <ReactECharts option={storageChart1Option} style={{ height: CH3_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>

              {/* 第4行：策略（全宽） */}
              <div style={{ ...chartBoxStyle, width: '100%', height: CH4_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  策略关联与电价对账
                </div>
                <ReactECharts option={storageChart2Option} style={{ height: CH4_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>
            </>
          ) : (
            <>
              {/* 多日：趋势 + 饼 + 电量结构（保持原版） */}
              <div style={{ ...chartBoxStyle, width: `calc(58% - ${G / 2}px)`, height: CH1_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  全场站日收益趋势
                </div>
                <ReactECharts option={trendOption} style={{ height: CH1_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>
              <div style={{ ...chartBoxStyle, width: `calc(42% - ${G / 2}px)`, height: CH1_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  收益结构占比
                </div>
                <ReactECharts option={pieOption} style={{ height: CH1_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>
              <div style={{ ...chartBoxStyle, width: '100%', height: CH2_H }}>
                <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                  电量结构对比
                </div>
                <ReactECharts option={barOption} style={{ height: CH2_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
              </div>
            </>
          )}
          {/* 配储分析图（2-7天多日） */}
          {!isSingleDay && hasBatteryChart && (
            <div style={{ ...chartBoxStyle, width: '100%', height: BATTERY_CHART_H }}>
              <div style={{ fontSize: 8, fontWeight: 600, marginBottom: 2, color: '#374151' }}>
                配储分析表
              </div>
              <ReactECharts option={batteryChartOption} style={{ height: BATTERY_CHART_H - 22 }} notMerge={true} opts={{ renderer: 'canvas' }} />
            </div>
          )}
        </div>

        {/* 汇总表 */}
        <div style={{ marginTop: G }}>
          <div style={{ fontSize: 9, fontWeight: 600, marginBottom: 2, color: '#374151' }}>按日汇总表</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {colgroup}
            {thead}
            {tbody}
          </table>
        </div>

        {/* 页脚 */}
        <div style={{ marginTop: 10, textAlign: 'right', fontSize: 8, color: '#9ca3af' }}>
          生成时间: {new Date().toLocaleString('zh-CN')}
        </div>
      </div>
    );
  }
);

RevenuePrintView.displayName = 'RevenuePrintView';

export default RevenuePrintView;
